/**
 * PHASE 2 STANDARDIZATION - Universal QuickView Provider
 * 
 * Standardized QuickView integration component that provides consistent
 * QuickView functionality across all product display components.
 * Eliminates duplicate modal instances and ensures uniform behavior.
 * 
 * Usage:
 * <QuickViewProvider>
 *   <YourProductComponents />
 * </QuickViewProvider>
 */

import React, { ReactNode } from 'react';
import { LazyQuickViewModal } from '@/components/LazyQuickViewModal';
import { initializeQuickViewOptimizations, preloadQuickView } from '@/utils/quickViewOptimization';
import { QuickViewContextProvider, useQuickViewContext } from '@/contexts/QuickViewContext';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';

interface QuickViewProviderProps {
  children: ReactNode;
}

const QuickViewModalContainer = ({ children }: QuickViewProviderProps) => {
  const { quickViewProduct, isQuickViewOpen, closeQuickView, handleViewProduct } = useQuickViewContext();
  const { addToRecentlyViewed } = useRecentlyViewed();

  // Initialize performance optimizations on mount
  React.useEffect(() => {
    initializeQuickViewOptimizations();
  }, []);

  const handleProductNavigation = () => {
    if (quickViewProduct) {
      // Add to recently viewed if it's a Shopify product
      if (quickViewProduct.handle) {
        addToRecentlyViewed(quickViewProduct as any);
      }
      
      // Navigate based on product type
      const productUrl = quickViewProduct.handle 
        ? `/shopify-product/${quickViewProduct.handle}`
        : `/product/${quickViewProduct.id}`;
      
      handleViewProduct(() => {
        if (typeof window !== 'undefined') {
          window.location.href = productUrl;
        }
      });
    }
  };

  return (
    <>
      <div 
        onMouseOver={() => preloadQuickView()}
        onTouchStart={() => preloadQuickView()}
      >
        {children}
      </div>
      
      {/* Universal Lazy QuickView Modal - Single instance for all components */}
      <LazyQuickViewModal
        product={quickViewProduct}
        isOpen={isQuickViewOpen}
        onClose={closeQuickView}
        onViewProduct={handleProductNavigation}
      />
    </>
  );
};

// Main QuickViewProvider that combines context and modal
export const QuickViewProvider = ({ children }: QuickViewProviderProps) => {
  return (
    <QuickViewContextProvider>
      <QuickViewModalContainer>
        {children}
      </QuickViewModalContainer>
    </QuickViewContextProvider>
  );
};

export default QuickViewProvider;